import { Injectable, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { ParkerService } from '../../api/gen/api/parker.service';
import { isJkuAccount, simulatedUserType } from '../jku-webshop/jku-user.service';

/**
 * Ensures the authenticated user has a backend `RegisteredParker` by calling
 * `POST /parker/me` (idempotent get-or-create) once after login.
 *
 * SSO users (e.g. JKU) skip `/parker/signup`, so this is their *only* provisioning
 * path — without it the webshop's `me/*` endpoints fail with "no registered parker".
 * It also links migrated email-only parkers and is the hook site-classification builds on.
 *
 * Fire-and-forget: a non-parker session (403) or transient error is ignored — provisioning
 * must never block app startup.
 */
@Injectable({ providedIn: 'root' })
export class ParkerProvisioningService {
  private readonly keycloak = inject(Keycloak);
  private readonly parker = inject(ParkerService);
  private ensured = false;

  /** Idempotent per app load; no-op when unauthenticated. */
  ensureParker(): void {
    if (this.ensured || !this.keycloak.authenticated) {
      return;
    }
    this.ensured = true;
    this.parker.parkerMePost().subscribe({
      next: () => this.classifyFromSso(),
      error: () => { /* provisioning failed (e.g. non-parker session) — skip classification */ }
    });
  }

  /**
   * After provisioning, auto-classify JKU SSO users at the JKU site (student/employee/other) from their
   * `user_type` claim. The backend derives and validates the classification from the token — this only
   * fires the trigger, and only for JKU sessions (an AK/K `jku_account` or a non-empty `user_type`).
   * The dev simulation (`jku.simulateSsoUser`) counts as a `user_type`, so a local account reaches the
   * call too; it is empty in production. Fire-and-forget.
   */
  private classifyFromSso(): void {
    const claims = this.keycloak.tokenParsed as Record<string, unknown> | undefined;
    const userType = claims?.['user_type'];
    const hasUserType = (typeof userType === 'string' && userType.trim() !== '') || simulatedUserType() !== '';
    if (!hasUserType && !isJkuAccount(claims?.['jku_account'])) {
      return; // not a JKU SSO session — nothing to classify
    }
    this.parker.parkerMeSiteClassificationPost().subscribe({
      next: () => { /* classified or no-op */ },
      error: () => { /* ignore — classification must never block the app */ }
    });
  }
}
